import {useAnalytics} from '@github-ui/use-analytics'
import {Button, Label} from '@primer/react'
import {type RefObject, useCallback, useRef} from 'react'
import {
  useSuggestionInBatch,
  useSuggestionBatchActions,
  useIsSuggestionBatchPending,
  useSuggestionBatch,
  usePullRequestBasePath,
  useComparisonInfo,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import type {SuggestedChange} from '../../../conversation-types'
import {saveActiveBatchToStorage} from '@github-ui/pull-requests/helpers/suggestion-batch-storage'

export function ApplyOrAddToBatch({
  onOpenDialog,
  suggestedChange,
}: {
  onOpenDialog: (returnFocusRef?: RefObject<HTMLElement | null>) => void
  suggestedChange: SuggestedChange
}) {
  const {sendAnalyticsEvent} = useAnalytics()
  const returnFocusRef = useRef<HTMLButtonElement>(null)

  const isPendingInBatch = useSuggestionInBatch({
    commentDatabaseId: suggestedChange.commentDatabaseId || 0,
    suggestionIndex: suggestedChange.suggestionIndex,
  })
  const isSuggestionBatchPending = useIsSuggestionBatchPending()
  const batchSuggestedChangesEnabled = useFeatureFlag('batch_suggested_changes')

  const {addSuggestionToBatch, removeSuggestionFromBatch} = useSuggestionBatchActions()
  const suggestionBatch = useSuggestionBatch()
  const pullRequestBasePath = usePullRequestBasePath()
  const comparisonInfo = useComparisonInfo()

  const handleAddToBatch = useCallback(() => {
    if (!suggestedChange.commentDatabaseId) {
      return
    }
    const suggestionId = {
      commentDatabaseId: suggestedChange.commentDatabaseId,
      suggestionIndex: suggestedChange.suggestionIndex,
    }
    addSuggestionToBatch(suggestionId)
    saveActiveBatchToStorage(pullRequestBasePath, comparisonInfo.fullDiff.headOid, [
      ...suggestionBatch.activeBatch,
      suggestionId,
    ])

    sendAnalyticsEvent('comments.add_suggested_change_to_batch', 'ADD_SUGGESTED_CHANGE_TO_BATCH_BUTTON')
  }, [
    addSuggestionToBatch,
    suggestedChange.commentDatabaseId,
    suggestedChange.suggestionIndex,
    suggestionBatch,
    pullRequestBasePath,
    comparisonInfo,
    sendAnalyticsEvent,
  ])

  const handleRemoveFromBatch = useCallback(() => {
    if (!suggestedChange.commentDatabaseId) {
      return
    }
    const suggestionId = {
      commentDatabaseId: suggestedChange.commentDatabaseId,
      suggestionIndex: suggestedChange.suggestionIndex,
    }
    removeSuggestionFromBatch(suggestionId)
    saveActiveBatchToStorage(
      pullRequestBasePath,
      comparisonInfo.fullDiff.headOid,
      suggestionBatch.activeBatch.filter(
        id =>
          !(
            id.commentDatabaseId === suggestionId.commentDatabaseId &&
            id.suggestionIndex === suggestionId.suggestionIndex
          ),
      ),
    )

    sendAnalyticsEvent('comments.remove_suggested_change_from_batch', 'REMOVE_SUGGESTED_CHANGE_FROM_BATCH_BUTTON')
  }, [
    removeSuggestionFromBatch,
    suggestedChange.commentDatabaseId,
    suggestedChange.suggestionIndex,
    suggestionBatch,
    pullRequestBasePath,
    comparisonInfo,
    sendAnalyticsEvent,
  ])

  const handleApplySuggestion = useCallback(() => {
    onOpenDialog(returnFocusRef)
  }, [onOpenDialog])

  return (
    <div className="d-flex flex-row flex-items-center">
      {batchSuggestedChangesEnabled && isPendingInBatch ? (
        <>
          <Label variant="attention">Pending in batch</Label>
          <div className="ml-auto">
            <Button variant="danger" onClick={handleRemoveFromBatch}>
              Remove from batch
            </Button>
          </div>
        </>
      ) : (
        <div className="d-flex gap-2 ml-auto">
          {!isSuggestionBatchPending && (
            <Button variant="default" onClick={handleApplySuggestion} ref={returnFocusRef}>
              Apply suggestion
            </Button>
          )}
          {batchSuggestedChangesEnabled && (
            <Button variant="primary" onClick={handleAddToBatch}>
              Add suggestion to batch
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

try{ ApplyOrAddToBatch.displayName ||= 'ApplyOrAddToBatch' } catch {}